import React, { useMemo } from "react";
import styled from "styled-components";
import { Tab, Card, Nav } from "react-bootstrap";
import { JSONSchema6 } from "json-schema";
import { ArrayWidgetProps } from ".";
import { FormFieldConfiguration } from "../crud/form";
import { defineMessages, FormattedMessage } from "react-intl";
import { usePropertyLocales } from "../../../components/property-locale-provider";
import "flag-icon-css/css/flag-icon.css";
import { SectionLabel } from "./SectionLabel";
import { mapLocaleToCountry } from "../../witadmin/Common/utils";


export function getLocaleCountryMap({ locales }: { locales: string[] }): Record<string, string> {
  return locales.reduce<Record<string, string>>((prev, locale) => {
    prev[locale] = mapLocaleToCountry(locale);
    return prev;
  }, {});
}

const translationWidgetMessages = defineMessages({
  someFieldsHaveErrors: {
    id: "default.someFieldsHaveErrors",
    defaultMessage: "Some fields have errors",
  },
});

const StyledTranslationCard = styled(Card)`
  max-width: 800px;

  .flag-icon-es-eu {
    background-image: url("https://s3.eu-central-1.amazonaws.com/witbooking.static/img/ikurrina.png");
    background-size: 28px;
  }

  .nav-tabs .errorTab .nav-link.active {
    background-color: #fbb4ac;
    border: 1px solid #fb6d81;
  }
  .errorTab {
    > a {
      background-color: #fbb4ac;
      border: 1px solid #fb6d81;
    }
  }
`;

export interface Translation {
  locale: string;
}

/**
 * Helper function to fully configure a translation list field on a form.
 * You may use the value of returned by this directly or compose it with further customization.
 */
export function translationFieldConfig<T extends Translation>(
  allowedLocales: string[]
): FormFieldConfiguration<T[]> {
  return {
    displayArrayAs: TranslationWidget,
    default: defaultTranslations(allowedLocales),
    transform: translationTransform(allowedLocales),
    children: {
      locale: {
        hidden: true,
      },
    },
  };
}

/**
 * Provides default empty placeholders for all valid locale translations.
 * @param allowedLocales Allowed locales.
 */
export function defaultTranslations<T extends Translation>(allowedLocales: string[]): T[] {
  return allowedLocales.map(locale => ({ locale: locale } as T));
}

export function translationTransform<T extends Translation>(
  allowedLocales: string[]
): (input: T[]) => T[] {
  return input =>
    allowedLocales.map(locale => {
      const item = input.find(i => i.locale === locale);
      if (item) {
        return item;
      } else {
        return { locale: locale } as T; //This cast isn't safe, but react-schema-form should do fine.
      }
    });
}

// Renders the translation widget.
// This widget always uses the inner schema to render the form
export function TranslationWidget<T extends Translation[]>(props: ArrayWidgetProps<T>) {
  const allowedLocales = usePropertyLocales();
  const localeCountryMap = useMemo(
    () =>
      getLocaleCountryMap({
        locales: allowedLocales,
      }),
    [allowedLocales]
  );

  const [tabs, hasErrors] = useMemo(() => {
    let someoneHasErrors = false;
    const tabs = allowedLocales.map(locale => {
      let item;
      let hasError = false;
      if (props.formData) {
        for (let i = 0; i < props.formData.length; i++) {
          const value = props.formData[i];
          if (value.locale === locale) {
            item = props.items[i];
            const errorSchemaObj = props.items[i].children.props.errorSchema;
            const errorSchemaKeys = Object.keys(props.items[i].children.props.errorSchema);
            hasError = errorSchemaKeys.some(key => errorSchemaObj[key]["__errors"]?.length > 0);
            someoneHasErrors = someoneHasErrors || hasError;
            break;
          }
        }
      }
      return [
        <Nav.Item key={locale} className={hasError ? "errorTab" : ""}>
          <Nav.Link eventKey={`locale-${locale}`}>
            <span className={`flag-icon flag-icon-${localeCountryMap[locale]}`} />
          </Nav.Link>
        </Nav.Item>,
        <Tab.Pane key={locale} eventKey={`locale-${locale}`}>
          {item && item.children}
        </Tab.Pane>,
      ];
    });
    return [tabs, someoneHasErrors];
  }, [props.items, props.formData, allowedLocales, localeCountryMap]);

  if (props.schema.type !== "array") {
    console.error("Invalid schema for translations widget. Must be array");
    return null;
  }
  const itemSchema = props.schema.items as JSONSchema6 | undefined;
  if (!itemSchema) {
    console.error("Translation widget must have an item schema.");
    return null;
  }

  return (
    <div id={props.idSchema.$id}>
      <SectionLabel>{props.schema.title}</SectionLabel>
      {hasErrors && (
        <ul className="error-detail mb">
          <li>
            <FormattedMessage {...translationWidgetMessages.someFieldsHaveErrors} />
          </li>
        </ul>
      )}
      <Tab.Container id={props.idSchema.$id} defaultActiveKey={`locale-${allowedLocales[0]}`}>
        <StyledTranslationCard>
          <Card.Header>
            <Nav variant="tabs">{tabs.map(t => t[0])}</Nav>
          </Card.Header>
          <Card.Body>
            <Tab.Content>{tabs.map(t => t[1])}</Tab.Content>
          </Card.Body>
        </StyledTranslationCard>
      </Tab.Container>
    </div>
  );
}
